import { base64UrlEncode } from "./kernel/kernel-encoding.js";
import { LOWER_UUID_PATTERN } from "./kernel/kernel-internal.js";
import { externalIssuerSchema, issuerNamesPrincipal, principalPublicJwkSchema } from "./federated-token.js";
import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/door-pull-principal.ts
/**
* The door-pull principal: one CP machine principal per data plane, with which that data plane pulls
* images through the CP docker door (2026-10-15-per-dp-door-identity, D1, D2, D8, D11, I1, I3).
*
* What it is for. Every data plane holds its own identity toward the docker door. provisioning-svc
* makes the principal, registers the data plane's KMS public key on it, mints the first pull tokens
* for it, and disables it when the data plane goes. cp-auth-api serves the five routes under
* {@link DOOR_PULL_PRINCIPALS_PATH} that do this, behind provisioning-svc's CP service token and the
* permission {@link DOOR_PULL_PRINCIPAL_MANAGE_PERMISSION}. The data plane's operator program signs
* its own assertions with the KMS key and exchanges them at `POST /v1/auth/token/federated`.
*
* The rules this module fixes for every caller:
*
* 1. One principal per data plane. Its id equals the data plane id, a lower-case UUID. So the
*    registered-key issuer of the principal, `https://<CP host>/machine-principals/<data plane id>`,
*    and the assertion `sub` are known to the operator program without a CP read
*    ({@link doorPullPrincipalIssuer}).
* 2. Its kind is {@link DOOR_PULL_PRINCIPAL_KIND}. A route under {@link DOOR_PULL_PRINCIPALS_PATH}
*    reaches no principal of another kind (F4). When a principal of another kind holds the id,
*    `ensure` answers the refusal `door_pull_principal_conflict`. `read`, `registerKey`, `disable`
*    and `mintToken` answer `door_pull_principal_not_found`, as for an id that no principal holds.
* 3. It holds exactly the two grants {@link DOOR_PULL_PRINCIPAL_ROLE_SLUGS}, both on the `platform`
*    resource. The routes grant no other role (F4).
* 4. Its trust policy names its registered-key issuer, the subject equal to the principal id, and the
*    one required claim {@link DOOR_PULL_DP_ID_CLAIM} equal to the data plane id (I3).
* 5. It holds at most one active key, a P-256 public JWK ({@link doorPullPublicJwkSchema}, F3). A new
*    key revokes the active one in the same write.
* 6. Disable is terminal. It revokes the active key. No route enables the principal again.
*
* Browser-safe: no Node builtin and no Node global. No schema uses `.transform()`, so each one stays
* representable in the JSON Schema that `createOpenApiDocument()` writes. No schema, helper or error
* message here ever carries a token, an assertion or a private key (F9).
*/
/**
* The principal kind of a door-pull principal, as cp-auth-api stores it on the machine principal
* row (migration 0048). Every route under {@link DOOR_PULL_PRINCIPALS_PATH} reads and writes only
* rows of this kind.
*/
const DOOR_PULL_PRINCIPAL_KIND = "door-pull";
/**
* The two roles a door-pull principal holds, each on the `platform` resource with the resource id
* `PLATFORM_RESOURCE_ID`, in this order:
*
* - `platform_registry_reader` holds `cp:platform:registry-read`: platform image reads on the door.
* - `app_registry_reader` holds `cp:app:registry-read`: app image reads on the door (D3).
*
* The ensure route grants both. No route under {@link DOOR_PULL_PRINCIPALS_PATH} grants any other
* role (F4).
*/
const DOOR_PULL_PRINCIPAL_ROLE_SLUGS = ["platform_registry_reader", "app_registry_reader"];
/**
* The permission on provisioning-svc's CP service token that every route under
* {@link DOOR_PULL_PRINCIPALS_PATH} requires (D11). cp-auth-api's `CP_SERVICE_TOKEN_PERMISSIONS`
* lists it for `provisioning-svc` and for no other subject.
*/
const DOOR_PULL_PRINCIPAL_MANAGE_PERMISSION = "cp:door-pull-principal:manage";
/**
* The permission the role `app_registry_reader` holds (D3). The docker door reads it from a
* `/v1/auth/me/grants` row on the `platform` resource and admits an `oci-app` or `oci-ping` read for
* it. The door is not a workspace package, so `proxy-lib.mjs` holds its own copy of the literal.
*/
const APP_REGISTRY_READ_PERMISSION = "cp:app:registry-read";
/**
* The one claim the trust policy of a door-pull principal requires, besides `iss` and `sub` (I3).
* Its value is the data plane id. The operator program puts it in every assertion it signs.
*/
const DOOR_PULL_DP_ID_CLAIM = "dp_id";
/**
* The key id of a door-pull key: the RFC 7638 SHA-256 thumbprint of its public JWK, in canonical
* unpadded base64url. 43 characters; the last one carries 4 data bits and 2 zero bits, so only 16
* characters can end it. cp-auth-api computes the same value with jose's `calculateJwkThumbprint`.
*/
const DOOR_PULL_KID_PATTERN = /^[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/;
/**
* A data plane id as every door-pull shape carries it: a lower-case UUID of any version, which is
* also the principal id (rule 1 of this module). An upper-case UUID, a UUID with braces, an empty
* string or a name such as `dp-acme` is a `ZodError`.
*/
const doorPullDataPlaneIdSchema = z.string().regex(LOWER_UUID_PATTERN, "a lower-case UUID");
/** A door-pull key id; see {@link DOOR_PULL_KID_PATTERN}. Any other string is a `ZodError`. */
const doorPullKidSchema = z.string().regex(DOOR_PULL_KID_PATTERN, "an RFC 7638 SHA-256 thumbprint, 43 canonical base64url characters");
/**
* The public JWK of a door-pull key (F3): `kty: "EC"`, `crv: "P-256"`, `x` and `y`, nothing else.
*
* It is the P-256 arm of `principalPublicJwkSchema` without the optional `alg`, so the two schemas
* share one coordinate grammar: each coordinate is 32 bytes in canonical unpadded base64url, 43
* characters. Strict: `alg`, `kid`, `use`, `key_ops`, the private member `d`, an Ed25519 key, an
* RSA key, a P-384 key and any other member is a `ZodError`. A point off the curve passes this
* schema; cp-auth-api imports the key with jose's `importJWK`, which refuses it, before it stores it.
*
* Callers: the enrollment request of `@zion/sdk-cp-dp-contracts` (the operator program sends it,
* release-engine parses it) and {@link doorPullKeyRegisterRequestSchema} (provisioning-svc sends it,
* cp-auth-api parses it).
*/
const doorPullPublicJwkSchema = principalPublicJwkSchema.options[1].omit({ alg: true }).strict();
/**
* The key id of a door-pull public JWK: the RFC 7638 SHA-256 thumbprint, in canonical unpadded
* base64url, 43 characters ({@link DOOR_PULL_KID_PATTERN}).
*
* Input: any value. The function parses it with {@link doorPullPublicJwkSchema} first.
*
* Output: `base64url(SHA-256('{"crv":"P-256","kty":"EC","x":"<x>","y":"<y>"}'))`, the RFC 7638
* member order with no whitespace. jose's `calculateJwkThumbprint(jwk, 'sha256')` gives the same
* text for the same key, which is the `kid` cp-auth-api stores and the JWS header must carry.
*
* Errors: throws the `ZodError` of {@link doorPullPublicJwkSchema} for a value that fails it.
* Pure: no clock, no I/O.
*/
function doorPullKeyThumbprint(jwk) {
	const key = doorPullPublicJwkSchema.parse(jwk);
	const canonical = `{"crv":"P-256","kty":"EC","x":"${key.x}","y":"${key.y}"}`;
	return base64UrlEncode(sha256(utf8ToBytes(canonical)));
}
/**
* The slug of the door-pull principal of `dataPlaneId`: `dp-<dataPlaneId>-door-pull`, 49
* characters, inside the machine principal slug grammar `^[a-z0-9][a-z0-9-]*[a-z0-9]$` of 3 to 64
* characters. It is the name an operator reads in an audit row and in `zion-ops`.
*
* Errors: throws `TypeError` when `dataPlaneId` is not a lower-case UUID. Pure.
*/
function doorPullPrincipalSlug(dataPlaneId) {
	assertDataPlaneId(dataPlaneId, "doorPullPrincipalSlug");
	return `dp-${dataPlaneId}-door-pull`;
}
/**
* The registered-key issuer of the door-pull principal of `dataPlaneId` (I3):
* `<cpOrigin>/machine-principals/<dataPlaneId>`. The operator program puts it in the `iss` of every
* assertion it signs; cp-auth-api names it in the principal's trust policy.
*
* Input:
* - `cpOrigin`: the canonical origin of the CP host that `CP_JWT_ISSUER` names, `https://<host>`
*   with an optional port other than 443, in lower case, with no path and no trailing slash. On the
*   internal CP that is `https://admin.internal.witness.ai`. The operator program reads the origin
*   of its exchange URL; cp-auth-api reads the origin of `CP_JWT_ISSUER`. When the two differ, the
*   exchange refuses every assertion, and the operator program reports `exchange-refused`.
* - `dataPlaneId`: a lower-case UUID.
*
* Output: the issuer, which `issuerNamesPrincipal(issuer, dataPlaneId)` answers `true` for.
*
* Errors: throws `TypeError` when `dataPlaneId` is not a lower-case UUID, or when the result is not
* an `externalIssuerSchema` issuer of that principal: `http://`, an upper-case host, a trailing
* slash, a path, a query, user info, the default port `:443` or an IP-literal host. Pure.
*/
function doorPullPrincipalIssuer(cpOrigin, dataPlaneId) {
	assertDataPlaneId(dataPlaneId, "doorPullPrincipalIssuer");
	const issuer = `${cpOrigin}/machine-principals/${dataPlaneId}`;
	if (!issuerNamesPrincipal(issuer, dataPlaneId)) throw new TypeError("doorPullPrincipalIssuer: cpOrigin must be a canonical https origin with a lower-case DNS host and no path");
	return issuer;
}
/** Throws `TypeError` naming `caller` unless `dataPlaneId` is a lower-case UUID. */
function assertDataPlaneId(dataPlaneId, caller) {
	if (!LOWER_UUID_PATTERN.test(dataPlaneId)) throw new TypeError(`${caller}: the data plane id must be a lower-case UUID`);
}
/** The prefix of the five cp-auth-api door-pull principal routes. Internal: the CP gateway routes no `/internal/*` path. */
const DOOR_PULL_PRINCIPALS_PATH = "/internal/door-pull-principals";
/**
* The five cp-auth-api door-pull principal routes, each with its method and its path template.
* Every route requires a CP service token of subject `provisioning-svc` that holds
* {@link DOOR_PULL_PRINCIPAL_MANAGE_PERMISSION}; cp-auth-api answers 401 without a token and 403 for
* any other subject or a token without the permission. Every write appends one audit row that names
* the data plane id (F10).
*
* - `ensure`: body {@link doorPullPrincipalEnsureRequestSchema}, answer
*   {@link doorPullPrincipalEnsureResponseSchema}. 201 with `outcome: 'created'` when the call made
*   the principal, 200 with `outcome: 'unchanged'` when it existed. In both cases the call converges
*   the trust policy and the two grants, and adds any that is missing. 409
*   `door_pull_principal_disabled` for a disabled principal, 409 `door_pull_principal_conflict` when
*   a machine principal of another kind holds the id or the slug, 409
*   `door_pull_principal_app_owner_required` when the call must revoke an app grant of the
*   principal and the revoke leaves the app without its owner. That refusal rolls the whole call
*   back, so a first `ensure` makes no principal. 400 when a reader role that the principal must
*   hold was widened: `assertPlatformGrantShape` refuses a string outside the platform strings, and
*   `assertDoorPullRoleShape` refuses a string outside the two door-pull reader strings.
* - `read`: answer {@link doorPullPrincipalViewSchema} with 200. 404 `door_pull_principal_not_found`.
* - `registerKey`: body {@link doorPullKeyRegisterRequestSchema}, answer
*   {@link doorPullKeyRegisterResponseSchema}. 201 with `outcome: 'registered'` when the key became
*   the active key, and any other active key was revoked in the same write; 200 with
*   `outcome: 'unchanged'` when the key was the active key. 404 `door_pull_principal_not_found`, 409
*   `door_pull_principal_disabled`, 409 `door_pull_key_revoked` for a key this principal revoked,
*   409 `door_pull_key_conflict` for a key another principal holds.
* - `disable`: body {@link doorPullPrincipalEmptyRequestSchema}, answer
*   {@link doorPullPrincipalViewSchema} with 200. It disables the principal and revokes its active
*   key. A second call answers the same view. 404 `door_pull_principal_not_found`.
* - `mintToken`: body {@link doorPullPrincipalEmptyRequestSchema}, answer `federatedTokenResponseSchema`
*   with 200: a CP token of `sub` `machine:<data plane id>` that lives `expires_in` seconds, at most
*   900. It is the bootstrap pull token that provisioning-svc writes for the first pull (D8). Before
*   it mints, the call converges the principal as `ensure` does. 404
*   `door_pull_principal_not_found`, 409 `door_pull_principal_disabled`, 409
*   `door_pull_principal_app_owner_required` and the widened-role 400 as for `ensure`, with no token.
*
* Every other 400: a body that fails its schema answers 400 from the validation pipe. A
* `:dataPlaneId` that is not a lower-case UUID answers 400 too.
*/
const DOOR_PULL_PRINCIPAL_ROUTES = {
	ensure: {
		method: "POST",
		path: "/internal/door-pull-principals"
	},
	read: {
		method: "GET",
		path: "/internal/door-pull-principals/:dataPlaneId"
	},
	registerKey: {
		method: "POST",
		path: "/internal/door-pull-principals/:dataPlaneId/keys"
	},
	disable: {
		method: "POST",
		path: "/internal/door-pull-principals/:dataPlaneId/disable"
	},
	mintToken: {
		method: "POST",
		path: "/internal/door-pull-principals/:dataPlaneId/tokens"
	}
};
/**
* The concrete path of `route` for `dataPlaneId`: the template of {@link DOOR_PULL_PRINCIPAL_ROUTES}
* with `:dataPlaneId` replaced. `ensure` names no data plane in its path, so its path is
* {@link DOOR_PULL_PRINCIPALS_PATH} for any `dataPlaneId`; the id still must be a lower-case UUID.
*
* Errors: throws `TypeError` when `dataPlaneId` is not a lower-case UUID. Pure.
*/
function doorPullPrincipalPath(route, dataPlaneId) {
	assertDataPlaneId(dataPlaneId, "doorPullPrincipalPath");
	return DOOR_PULL_PRINCIPAL_ROUTES[route].path.replace(":dataPlaneId", dataPlaneId);
}
/** The body of `ensure`: the data plane whose principal the call makes or converges. Strict. */
const doorPullPrincipalEnsureRequestSchema = z.object({ dataPlaneId: doorPullDataPlaneIdSchema.describe("The data plane; also the principal id") }).strict();
/** The body of `registerKey`: the public JWK the operator program enrolled with release-engine. Strict. */
const doorPullKeyRegisterRequestSchema = z.object({ publicJwk: doorPullPublicJwkSchema.describe("The public half of the data plane KMS door key") }).strict();
/** The body of `disable` and `mintToken`: the empty object. Strict, so any key answers 400. */
const doorPullPrincipalEmptyRequestSchema = z.object({}).strict();
/**
* The view of one door-pull principal, as every route but `mintToken` answers it.
*
* - `principalId` and `dataPlaneId`: the same lower-case UUID (rule 1).
* - `kind`: {@link DOOR_PULL_PRINCIPAL_KIND}.
* - `slug`: {@link doorPullPrincipalSlug} of the data plane.
* - `issuer`: the registered-key issuer of the principal, from `CP_JWT_ISSUER`. It names
*   `principalId` (`issuerNamesPrincipal`).
* - `disabled`: `true` after `disable`.
* - `roles`: the reader roles the principal holds, each once, in
*   {@link DOOR_PULL_PRINCIPAL_ROLE_SLUGS} order. After `ensure` it holds both.
* - `activeKid`: the key id of the active key, or `null` when the principal holds no active key.
* - `revokedKeyCount`: how many keys of the principal were revoked, from 0.
* - `createdAt`: when cp-auth-api made the principal, an ISO 8601 UTC timestamp.
*
* Strict. Refused, with a path to the member: an issuer that names another principal, a
* `principalId` other than `dataPlaneId`, a slug other than the slug of the data plane, a repeated
* role or roles out of order, and a disabled principal with an active key.
*/
const doorPullPrincipalViewSchema = z.object({
	principalId: doorPullDataPlaneIdSchema,
	dataPlaneId: doorPullDataPlaneIdSchema,
	kind: z.literal(DOOR_PULL_PRINCIPAL_KIND),
	slug: z.string().min(3).max(64),
	issuer: externalIssuerSchema,
	disabled: z.boolean(),
	roles: z.array(z.enum(DOOR_PULL_PRINCIPAL_ROLE_SLUGS)).max(DOOR_PULL_PRINCIPAL_ROLE_SLUGS.length),
	activeKid: doorPullKidSchema.nullable(),
	revokedKeyCount: z.number().int().min(0),
	createdAt: z.iso.datetime()
}).strict().superRefine((view, ctx) => {
	if (view.principalId !== view.dataPlaneId) ctx.addIssue({
		code: "custom",
		path: ["principalId"],
		message: "the principal id must equal the data plane id"
	});
	if (view.slug !== `dp-${view.dataPlaneId}-door-pull`) ctx.addIssue({
		code: "custom",
		path: ["slug"],
		message: "the slug must be dp-<dataPlaneId>-door-pull"
	});
	if (!issuerNamesPrincipal(view.issuer, view.principalId)) ctx.addIssue({
		code: "custom",
		path: ["issuer"],
		message: "the issuer names another principal"
	});
	const order = view.roles.map((role) => DOOR_PULL_PRINCIPAL_ROLE_SLUGS.indexOf(role));
	if (order.some((position, i) => i > 0 && position <= order[i - 1])) ctx.addIssue({
		code: "custom",
		path: ["roles"],
		message: "each role once, in DOOR_PULL_PRINCIPAL_ROLE_SLUGS order"
	});
	if (view.disabled && view.activeKid !== null) ctx.addIssue({
		code: "custom",
		path: ["activeKid"],
		message: "a disabled principal holds no active key"
	});
});
/** The outcomes of `ensure`: the call made the principal, or the principal existed. */
const DOOR_PULL_PRINCIPAL_ENSURE_OUTCOMES = ["created", "unchanged"];
/**
* The answer of `ensure`: the outcome and the view after the call. Strict. Two views are a
* `ZodError`:
*
* - a disabled principal, path `principal.disabled`, because `ensure` refuses a disabled principal
*   with 409;
* - a principal that does not hold both reader roles, path `principal.roles`, because `ensure`
*   writes both grants in the call that answers (contract rule 3). A server that skipped a grant
*   then fails the caller's parse, and provisioning-svc never mints a token that the door refuses.
*/
const doorPullPrincipalEnsureResponseSchema = z.object({
	outcome: z.enum(DOOR_PULL_PRINCIPAL_ENSURE_OUTCOMES),
	principal: doorPullPrincipalViewSchema
}).strict().superRefine((answer, ctx) => {
	if (answer.principal.disabled) ctx.addIssue({
		code: "custom",
		path: ["principal", "disabled"],
		message: "ensure never answers a disabled principal"
	});
	if (answer.principal.roles.length !== DOOR_PULL_PRINCIPAL_ROLE_SLUGS.length) ctx.addIssue({
		code: "custom",
		path: ["principal", "roles"],
		message: "after ensure the principal holds both reader roles"
	});
});
/** The outcomes of `registerKey`: the key became the active key, or it was the active key. */
const DOOR_PULL_KEY_REGISTER_OUTCOMES = ["registered", "unchanged"];
/**
* The answer of `registerKey`: the outcome and the view after the call. Strict. A view without an
* active key is a `ZodError`, because both outcomes leave the registered key active.
*/
const doorPullKeyRegisterResponseSchema = z.object({
	outcome: z.enum(DOOR_PULL_KEY_REGISTER_OUTCOMES),
	principal: doorPullPrincipalViewSchema
}).strict().refine((answer) => answer.principal.activeKid !== null, {
	message: "both outcomes leave an active key",
	path: ["principal", "activeKid"]
});
/**
* The coded refusals of the door-pull principal routes. cp-auth-api answers each one with a 4xx
* body whose `error` is the code; the shared exception filter adds `statusCode`, `message`,
* `timestamp` and `path`.
*
* - `door_pull_principal_not_found` (404): no door-pull principal exists for the data plane. A
*   principal of another kind on the id gives this refusal on every route but `ensure` (rule 2).
* - `door_pull_principal_disabled` (409): the principal is disabled; only `read` and `disable` still
*   answer it.
* - `door_pull_principal_conflict` (409): `ensure` found a machine principal of another kind that
*   holds the id or the slug (F4).
* - `door_pull_key_revoked` (409): the key was revoked on this principal; a revoked key never
*   becomes active again.
* - `door_pull_key_conflict` (409): another machine principal holds the key, under the global
*   unique `kid`.
* - `door_pull_principal_app_owner_required` (409): `ensure` or `mintToken` must revoke an app
*   grant of the principal (contract rule 3), and the revoke leaves that app without the owner it
*   must keep. An app must keep one active `app_owner` grant, whatever the role of the revoked
*   grant: the #838 rule of the admin revoke and the owner trigger of cp-auth-api both count
*   `app_owner` alone since migration 0051 (2026-10-20-cp-permissioning R-025 item 3). The call
*   changes nothing. The remedy: grant `app_owner` on that app to another principal; the next call
*   then revokes the door-pull principal's grant.
*/
const DOOR_PULL_PRINCIPAL_ERRORS = [
	"door_pull_principal_not_found",
	"door_pull_principal_disabled",
	"door_pull_principal_conflict",
	"door_pull_key_revoked",
	"door_pull_key_conflict",
	"door_pull_principal_app_owner_required"
];
/** The HTTP status of each coded refusal. */
const DOOR_PULL_PRINCIPAL_ERROR_STATUS = Object.freeze({
	door_pull_principal_not_found: 404,
	door_pull_principal_disabled: 409,
	door_pull_principal_conflict: 409,
	door_pull_key_revoked: 409,
	door_pull_key_conflict: 409,
	door_pull_principal_app_owner_required: 409
});
/**
* The coded refusal a cp-auth-api answer body carries, for a client of the door-pull routes.
*
* Input: the parsed JSON body of any answer.
*
* Output: the code when the body is an object whose `error` is one of
* {@link DOOR_PULL_PRINCIPAL_ERRORS} and whose `statusCode` is the status
* {@link DOOR_PULL_PRINCIPAL_ERROR_STATUS} gives that code; `null` for every other body: a success
* body, a 400 of the validation pipe, a 401 or 403 of the guards, a 5xx, a non-object, and a code
* with the wrong status. A caller treats `null` on a non-2xx answer as an uncoded failure.
*
* Never throws. Pure.
*/
function doorPullPrincipalRefusalOf(body) {
	if (typeof body !== "object" || body === null) return null;
	const { error, statusCode } = body;
	if (typeof error !== "string" || !DOOR_PULL_PRINCIPAL_ERRORS.includes(error)) return null;
	const code = error;
	return DOOR_PULL_PRINCIPAL_ERROR_STATUS[code] === statusCode ? code : null;
}
/**
* The error a {@link DoorPullPrincipalApi} implementation throws for a coded refusal. `status` is
* the HTTP status of `code`; `message` names the code and nothing else.
*/
var DoorPullPrincipalRefusal = class extends Error {
	code;
	status;
	constructor(code) {
		super(`door-pull principal refused: ${code}`);
		this.name = "DoorPullPrincipalRefusal";
		this.code = code;
		this.status = DOOR_PULL_PRINCIPAL_ERROR_STATUS[code];
	}
};
//#endregion
export { APP_REGISTRY_READ_PERMISSION, DOOR_PULL_DP_ID_CLAIM, DOOR_PULL_KEY_REGISTER_OUTCOMES, DOOR_PULL_KID_PATTERN, DOOR_PULL_PRINCIPALS_PATH, DOOR_PULL_PRINCIPAL_ENSURE_OUTCOMES, DOOR_PULL_PRINCIPAL_ERRORS, DOOR_PULL_PRINCIPAL_ERROR_STATUS, DOOR_PULL_PRINCIPAL_KIND, DOOR_PULL_PRINCIPAL_MANAGE_PERMISSION, DOOR_PULL_PRINCIPAL_ROLE_SLUGS, DOOR_PULL_PRINCIPAL_ROUTES, DoorPullPrincipalRefusal, doorPullDataPlaneIdSchema, doorPullKeyRegisterRequestSchema, doorPullKeyRegisterResponseSchema, doorPullKeyThumbprint, doorPullKidSchema, doorPullPrincipalEmptyRequestSchema, doorPullPrincipalEnsureRequestSchema, doorPullPrincipalEnsureResponseSchema, doorPullPrincipalIssuer, doorPullPrincipalPath, doorPullPrincipalRefusalOf, doorPullPrincipalSlug, doorPullPrincipalViewSchema, doorPullPublicJwkSchema };

//# sourceMappingURL=door-pull-principal.js.map